/**
 * The ≈₽ rates behind the matrix's forecast totals — the React half.
 *
 * Two inputs sit in the toolbar next to "Show forecasted values" (USD→RUB and
 * EUR→RUB). Their default is the freshest rate already on screen, whatever the
 * user types is remembered per browser, and a cleared input means "no rate":
 * that currency's ₽ total shows "—". The rules live in `forecast-rates.ts`,
 * which is where the unit tests are.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  FORECAST_RATES_KEY,
  FORECAST_RATE_CURRENCIES,
  forecastRateInputs,
  readStoredRateInput,
  resolveForecastRates,
  type ForecastRateCurrency,
  type StoredRateInput,
} from './forecast-rates';

export { FORECAST_RATES_KEY, FORECAST_RATE_CURRENCIES };
export type { ForecastRateCurrency };

export interface ForecastRates {
  /** The rates the totals actually use (user input wins over last-known). */
  rates: Record<string, number>;
  /** What each input displays, as typed. */
  inputs: Record<ForecastRateCurrency, string>;
  /** One currency at a time; `''` clears it. */
  setRate: (currency: ForecastRateCurrency, value: string) => void;
}

export function useForecastRates(lastKnown: Record<string, number>): ForecastRates {
  const [input, setInput] = useState<StoredRateInput>(() => readStoredRateInput(window.localStorage));

  useEffect(() => {
    try {
      window.localStorage.setItem(FORECAST_RATES_KEY, JSON.stringify(input));
    } catch {
      // Unpersistable is not unusable — keep the in-memory state.
    }
  }, [input]);

  const setRate = useCallback((currency: ForecastRateCurrency, value: string) => {
    setInput((current) => ({ ...current, [currency]: value }));
  }, []);

  return {
    rates: resolveForecastRates(input, lastKnown),
    inputs: forecastRateInputs(input, lastKnown),
    setRate,
  };
}
